import React, { useEffect } from 'react'
import { Alert } from '@mui/material'

import { CommonFilters } from '@components'
import {analyzerDate, offices, reports, searchID, sites} from '@signals'
import {hasNoCurrencyResults, isAnalyzerLoading, resetSignals, tableTabSelected} from 'Pages/AnalyzersV2/@signals'
import { BACKEND_APIS } from '@constants'

export default function AnalyzerCommonFilters({ onSubmit, components, schema, isValid, errors, compProps = {}, getDirectSites }) {

	useEffect(() => {
		resetSignals()
		return () => {
			isAnalyzerLoading.value = false
		}
	}, [reports.value])

	const hasValidDates = Array.isArray(analyzerDate.value)
		? analyzerDate.value.length > 0
		: !!analyzerDate.value


	useEffect(() => {
		if ((isValid && !!reports.value) || (!!searchID.value && hasValidDates)) {
			onSubmit(false)
		}
	}, [tableTabSelected.value])

	const hasDirectSites = window.location.pathname.includes('hasDirectSites')
	const hasBrokers = window.location.pathname.includes('hasCompetitionSites')
	const reportExecutionID = reports.value
	const searchIDUsed = searchID.value
	const officePickupID = offices.value
	const siteID = sites.value
	const hasOfficeOrSearch = (!!officePickupID && !!reportExecutionID) || !!searchIDUsed

	// The pathname flags are how the older analyzers declare which family of webs they read;
	// a page with a Direct Sites / Broker toggle passes the current mode instead. Every option
	// of `/master-data/execution/options/*` is scoped by it, not only the site list: without it
	// the dates, LORs, providers or acriss of the other family would still be offered.
	const directSitesFilter = getDirectSites ?? ((hasDirectSites || hasBrokers) ? hasDirectSites : undefined)

	const filterCommonDependencies = {
		filters: {
			...!searchIDUsed ? { officePickupID } : { searchID: searchIDUsed },
			...directSitesFilter != null && { getDirectSites: directSitesFilter },
			reportExecutionID,
		},
	}

	const filtersWithSiteAndMain = {
		...filterCommonDependencies.filters,
		siteID: siteID,
	}

	const autocompleteProps = {
		...filterCommonDependencies,
	}

	const noResults = hasNoCurrencyResults.value
	const disabledByNoResults = hasOfficeOrSearch && noResults ? { disabled: true } : {}

	const gridProps = { xs: 2 }
	return (
		<>
		<CommonFilters
			components={{
				priceKpiToggle: hasOfficeOrSearch,
				webProviderToggle: hasOfficeOrSearch,
				offices: !!reportExecutionID,
				mainSite: hasOfficeOrSearch,
				sites: hasOfficeOrSearch,
				categories: hasOfficeOrSearch,
				providers: hasOfficeOrSearch,
				tiers: hasOfficeOrSearch,
				currency: hasOfficeOrSearch,
				showFeatures: hasOfficeOrSearch,
				analyzerAcriss: hasOfficeOrSearch,
				mileage: hasOfficeOrSearch,
				transmission: hasOfficeOrSearch,
				fuelPolicy: hasOfficeOrSearch,
				insurance: hasOfficeOrSearch,
				prepaid: hasOfficeOrSearch,
				inTerminal: hasOfficeOrSearch,
				webSuppliers: hasOfficeOrSearch,
				// Plain optional multi select: the analyzers have no negation for
				// `id_master_web_supplier` (only reports extras do), so the
				// include/exclude selector stays out.
				hideExcludeWebSuppliers: true,
				// TEMPORARY: hidden until the backend decides what an empty dropoff
				// means (see `_set_optional_filters` in base_filters_service.py). The
				// slot and the payload key stay wired, so re-enabling is one line.
				dropoffOffices: false,
				dates: hasOfficeOrSearch,
				mainProvider: hasOfficeOrSearch,
				...components,
			}}
			compProps={{
				// The toggle labels are analyzer-specific (position renames the two
				// modes), so whatever the page sets goes straight through.
				directSiteBrokerProviderToggle: { ...compProps?.directSiteBrokerProviderToggle },
				showFeatures: { ...compProps?.showFeatures },
				offices: {
					error: errors.officePickupID,
					urlAjax: `${BACKEND_APIS.EXECUTION_INFO}/offices`,
					filters: { idReportExecution: reportExecutionID, selected: officePickupID },
					notAsync: true,
				},
				mainSite: {
					...autocompleteProps,
					...disabledByNoResults,
					// `siteMainID` is the payload key: analyzers that validate it
					// directly (position) surface the error under that name.
					error: errors.mainSite || errors.siteMainID,
					...hasDirectSites && { label: 'Main Site' },
					filters: { ...filtersWithSiteAndMain, siteID: null },
					...compProps?.mainSite,
					...(compProps?.mainSite?.filters && {
						filters: { ...filtersWithSiteAndMain, siteID: null, ...compProps.mainSite.filters },
					}),
				},
				sites: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.sites || errors.siteID,
					multiple: !hasBrokers,
					...compProps?.sites,
					...(compProps?.sites?.filters && {
						filters: { ...filterCommonDependencies.filters, ...compProps.sites.filters },
					}),
				},
				categories: {
					...autocompleteProps,
					...disabledByNoResults,
					// `categoryID` is the payload key: One Way validates it directly when the
					// header groups by category, and surfaces the error under that name.
					error: errors.categories || errors.categoryID,
					filters: filtersWithSiteAndMain,
					...compProps?.categories,
				},
				// Only the width and the layout order: the options come from the analyzer's own
				// group by dimension, which needs no execution scoped ajax.
				acrissGroupBy: { ...compProps?.acrissGroupBy },
				mainProvider: {
					...autocompleteProps,
					...disabledByNoResults,
					grid: { xs: 3 },
					error: errors.mainProvider || errors.providerMainID,
					filters: filtersWithSiteAndMain,
					...compProps?.mainProvider,
					...(compProps?.mainProvider?.filters && {
						filters: { ...filtersWithSiteAndMain, ...compProps.mainProvider.filters },
					}),
				},
				providers: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.providerID,
					grid: { ...gridProps, ...compProps?.providers?.grid },
					filters: filtersWithSiteAndMain,
				},
				tiers: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.tiers,
					grid: gridProps,
					// a per-user list: the execution filters only made it re-fetch on every office, site or toggle change
					filters: {},
				},
				dates: {
					...disabledByNoResults,
					error: errors.dates,
					disableClearable: true,
					label: 'Pickup Date',
					multiple: false,
					filters: filtersWithSiteAndMain,
					...compProps.dates,
				},
				lengthRental: {
					...autocompleteProps,
					...disabledByNoResults,
					grid: { ...gridProps, ...compProps?.lengthRental?.grid },
					filters: filtersWithSiteAndMain,
					...compProps?.lengthRental,
				},
				currency: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.currency,
					filters: filtersWithSiteAndMain,
					...compProps?.currency,
					onFinishAjax: options => { hasNoCurrencyResults.value = options.length === 0 },
				},
				analyzerAcriss: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.acriss,
					filters: filtersWithSiteAndMain,
					...compProps?.analyzerAcriss,
				},
				acrissGroups: {
					...disabledByNoResults,
					error: errors.acrissGroupID,
					...compProps?.acrissGroups,
				},
				mileage: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.mileage,
					filters: filtersWithSiteAndMain,
				},
				transmission: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.transmission,
					filters: filtersWithSiteAndMain,
				},
				fuelPolicy: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.fuelPolicy,
					filters: filtersWithSiteAndMain,
				},
				insurance: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.insurance,
					// filtered by site only on the backend; the office/execution keys just caused refetches
					filters: { siteID },
				},
				prepaid: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.prepaid,
					filters: filtersWithSiteAndMain,
				},
				inTerminal: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.inTerminal,
					// a static list: no filter is read on the backend
					filters: {},
				},
				webSuppliers: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.webSupplierID,
					grid: { xs: 3 },
					// The shared component defaults to the user-scoped list, which the
					// reports drawer needs; analyzers list what the execution actually has.
					urlAjax: `${BACKEND_APIS.MASTER_DATA}/execution/options/web_suppliers`,
					filters: filtersWithSiteAndMain,
				},
				dropoffOffices: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.officeDropoffID,
					filters: filtersWithSiteAndMain,
					...compProps?.dropoffOffices,
				},
			}}
			isValid={isValid}
			onSubmit={() => onSubmit(true)}
			schema={schema}
			isLoading={isAnalyzerLoading.value}
		/>
		{noResults && (
			<Alert severity="warning" sx={{ mt: 1 }}>
				No results available for this office. Change office or execution to get results.
			</Alert>
		)}
		</>
	)
}
